// lib/marketplace/first-listing.ts
// Turns optional signup role-intent into a publishable first-listing payload.
// Season is a best-effort map from the signup broad bucket to a specific
// SEASON_TYPES id (the listing is editable; the employer sees the title
// preview before opting in). Locations are left empty (any) for honesty.

import { expandRoleCategoriesToLeafIds } from "@/lib/taxonomy";

export interface FirstListingIntent {
  roleCategory: string;
  seasonTypes: string[];
  region: string | null;
  titleOverride?: string;
}

export interface FirstListingPayload {
  title: string;
  description: string;
  roles: string[];
  locations: string[];
  season_type: string;
  response_mode: "interest";
  dates_flexible: true;
}

const ROLE_CATEGORY_LABELS: Record<string, string> = {
  ski: "Ski & mountain",
  yacht: "Yacht crew",
  watersports: "Watersports",
  festivals: "Festival & events",
  agriculture: "Agriculture",
  hospitality: "Hospitality",
};

/**
 * Closing sentence of the auto-published signup listing. While it is still in
 * the description the employer has not written the listing themselves, so
 * the listing is treated as generic (for example, it never triggers job
 * alerts). Editing the description removes the marker.
 */
export const GENERIC_FIRST_LISTING_MARKER = "(Edit this listing to add detail.)";

export function isGenericFirstListing(listing: {
  description: string | null;
}): boolean {
  return (listing.description ?? "").includes(GENERIC_FIRST_LISTING_MARKER);
}

// Broad signup bucket to a sensible default specific season_type id.
export const SEASON_BUCKET_TO_TYPE: Record<string, string> = {
  winter: "alpine-winter",
  summer: "med-summer",
  festivals: "eu-festivals",
};

function resolveSeasonType(seasonTypes: string[]): string | null {
  for (const bucket of seasonTypes) {
    const id = SEASON_BUCKET_TO_TYPE[bucket];
    if (id) return id;
  }
  return null;
}

function seasonLabel(seasonTypes: string[]): string {
  if (seasonTypes.includes("winter")) return "winter season";
  if (seasonTypes.includes("summer")) return "summer season";
  if (seasonTypes.includes("festivals")) return "festival season";
  return "the season";
}

export function buildFirstListingTitle(intent: FirstListingIntent): string {
  if (intent.titleOverride?.trim()) return intent.titleOverride.trim();
  const role = ROLE_CATEGORY_LABELS[intent.roleCategory] ?? "Seasonal";
  const where = intent.region ? `, ${intent.region}` : "";
  return `${role} roles${where}`;
}

export function buildFirstListingPayload(
  intent: FirstListingIntent
): FirstListingPayload | null {
  const season_type = resolveSeasonType(intent.seasonTypes);
  if (!season_type) return null;

  const roles = intent.roleCategory
    ? expandRoleCategoriesToLeafIds([intent.roleCategory])
    : [];
  const title = buildFirstListingTitle(intent);
  const where = intent.region ? ` in ${intent.region}` : "";
  const description =
    `We are hiring for the ${seasonLabel(intent.seasonTypes)}${where}. ` +
    `Express interest and we will be in touch. ${GENERIC_FIRST_LISTING_MARKER}`;

  return {
    title,
    description,
    roles,
    locations: [],
    season_type,
    response_mode: "interest",
    dates_flexible: true,
  };
}
